/**
 * Serviço unificado para upload de vídeos
 * Envia vídeos ao armazenamento da Hostinger.
 */

import { getAccessToken } from './authApi';

const HOSTINGER_API_URL = 'https://nutrisync.dgapp.com.br/api/upload-video.php';
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 segundo
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB

export interface VideoUploadResponse {
  success: boolean;
  url?: string;
  thumbnailUrl?: string;
  error?: string;
  source?: 'hostinger';
}

/**
 * Aguarda um tempo específico (para retry com backoff)
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Testa a conectividade com a API do Hostinger
 */
export async function testHostingerVideoAPI(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 segundos timeout

    const response = await fetch(HOSTINGER_API_URL, {
      method: 'OPTIONS',
      headers: {
        'Accept': 'application/json',
      },
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    return response.ok || response.status === 200;
  } catch (error) {
    console.warn('[VideoUpload] Teste de conectividade com Hostinger falhou:', error);
    return false;
  }
}

/**
 * Faz upload de um vídeo para o Hostinger
 */
async function uploadToHostinger(
  file: File,
  userId: string,
  folder: string = 'videos',
  retryCount: number = 0
): Promise<{ url: string; thumbnailUrl?: string }> {
  try {
    const formData = new FormData();
    formData.append('video', file);
    formData.append('userId', userId);
    formData.append('folder', folder);

    console.log(`[VideoUpload] Hostinger - Tentativa ${retryCount + 1}/${MAX_RETRIES + 1}`);
    console.log(`[VideoUpload] Arquivo: ${file.name}, Tamanho: ${file.size} bytes`);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutos timeout para vídeos

    const accessToken = getAccessToken();
    const response = await fetch(HOSTINGER_API_URL, {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    const responseText = await response.text();
    const contentType = response.headers.get('content-type') || '';

    console.log(`[VideoUpload] Hostinger - Status: ${response.status}, Content-Type: ${contentType}`);

    if (!response.ok) {
      if (response.status === 500 && retryCount < MAX_RETRIES) {
        const delayMs = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
        console.log(`[VideoUpload] Hostinger - Erro 500, tentando novamente em ${delayMs}ms...`);
        await delay(delayMs);
        return uploadToHostinger(file, userId, folder, retryCount + 1);
      }
      
      throw new Error(`HTTP ${response.status}: ${responseText.substring(0, 200)}`);
    }

    if (responseText.trim().startsWith('<') || !contentType.includes('application/json')) {
      console.error('[VideoUpload] Hostinger retornou HTML em vez de JSON');
      throw new Error('Servidor retornou resposta inválida');
    }

    let result: VideoUploadResponse;
    try {
      result = JSON.parse(responseText);
    } catch (parseError) {
      console.error('[VideoUpload] Erro ao parsear JSON:', responseText.substring(0, 200));
      throw new Error('Resposta do servidor inválida');
    }

    if (!result.success || !result.url) {
      throw new Error(result.error || 'Upload falhou sem URL');
    }

    console.log(`[VideoUpload] Hostinger - Upload bem-sucedido: ${result.url}`);
    return {
      url: result.url,
      thumbnailUrl: result.thumbnailUrl,
    };
  } catch (error: any) {
    const isNetworkError = 
      error.name === 'AbortError' ||
      error.message?.includes('Network') || 
      error.message?.includes('timeout') ||
      error.message?.includes('fetch');

    if (isNetworkError && retryCount < MAX_RETRIES) {
      const delayMs = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
      console.log(`[VideoUpload] Hostinger - Erro de rede, tentando novamente em ${delayMs}ms...`);
      await delay(delayMs);
      return uploadToHostinger(file, userId, folder, retryCount + 1);
    }
    
    console.error('[VideoUpload] Hostinger - Erro final:', error);
    throw error;
  }
}

/**
 * Gera thumbnail de um vídeo (usando canvas)
 */
function generateVideoThumbnail(videoFile: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      video.currentTime = 1; // Pegar frame no segundo 1
    };

    video.onseeked = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
      const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
      resolve(thumbnail);
    };

    video.onerror = (error) => {
      reject(new Error('Erro ao gerar thumbnail do vídeo'));
    };

    video.src = URL.createObjectURL(videoFile);
  });
}

/**
 * Upload de vídeo para a Hostinger.
 * 
 * @param file - Arquivo de vídeo a ser enviado
 * @param userId - ID do usuário que está fazendo o upload
 * @param folder - Pasta de destino (padrão: 'videos')
 * @returns Objeto com a URL do vídeo, thumbnail e a fonte Hostinger
 */
export async function uploadVideo(
  file: File,
  userId: string,
  folder: string = 'videos'
): Promise<VideoUploadResponse> {
  // Validar arquivo
  if (!file) {
    throw new Error('Nenhum arquivo fornecido');
  }

  if (!file.type.startsWith('video/')) {
    throw new Error('Arquivo deve ser um vídeo');
  }

  if (file.size > MAX_VIDEO_SIZE) {
    throw new Error(`Vídeo deve ter no máximo ${MAX_VIDEO_SIZE / (1024 * 1024)}MB`);
  }

  console.log(`[VideoUpload] Iniciando upload: ${file.name} (${file.size} bytes)`);

  const { url, thumbnailUrl } = await uploadToHostinger(file, userId, folder);
  return { success: true, url, thumbnailUrl, source: 'hostinger' };
}



